// Record pages: the RecordPage shell (record-page redesign step 9; docs/record-page-redesign.md
// "## Shell" → Header, Columns, Responsive). Mounted nowhere yet: steps 10-13 render the deal,
// company and contact pages through it behind recordV2.
//
// One fixed header — row 1 (44px, also the compact bar) plus an overlay (meta line, banner,
// band) that fades when the center scrolls — over three independently scrolling columns.
// Every column starts with a spacer as tall as the overlay, so nothing moves when it fades.
// The layout math is pure in record_panels_core.jsx (recordGridPlan, recordShellStyles, …;
// test/record_shell.test.mjs). Cross-file names are read from window at render time.

const shellCore = () => window.RecordPanelsCore;

// ── storage: two keys per kind, every access guarded (the page works without storage)
const _lsGet = (k) => { try { return window.localStorage.getItem(k); } catch (_) { return null; } };
const _lsSet = (k, v) => { try { window.localStorage.setItem(k, v); } catch (_) { /* private mode: memory only */ } };
const touchNow = () => !!(window.AtlasDevice && window.AtlasDevice.touch);
const widthsKey = (kind) => shellCore().PANES_KEY + kind;
const collapsedKey = (kind) => shellCore().PANES_KEY + kind + '.collapsed';
// Mouse only. A deal with no saved widths takes the classic page's (atlas.dealPanes)
// once; that key stays: the classic page reads it until step 14.
const loadWidths = (kind) => {
  const core = shellCore();
  if (touchNow()) return core.readPaneWidths(null, kind);
  let raw = _lsGet(widthsKey(kind));
  if (raw == null && kind === 'deal') {
    const seed = core.migrateDealPanes(_lsGet(core.DEAL_PANES_KEY));
    if (seed) { raw = JSON.stringify(seed); _lsSet(widthsKey(kind), raw); }
  }
  return core.readPaneWidths(raw, kind);
};
// Touch never saves widths (no dividers there; a tablet sizes from its body).
const saveWidths = (kind, w) => {
  if (touchNow()) return;
  _lsSet(widthsKey(kind), JSON.stringify({ L: w.L, R: w.R }));
};

// ── composer host: which surface renders the record's one composer, in memory per
// record ('pane' by default). Step 10's workspace Log ▾ claims 'workspace', so two
// editors never share one draft.
// reveal: a compose request (Log ▾, Next action 'Update', a feed drop) focuses the open
// pane's field, as the classic inline composer did (RecordNotes.revealComposer calls it;
// a caller that opens the draft itself calls it too).
const _composerHosts = new Map();
const _composerHostSubs = new Set();
const _composerRevealSubs = new Set();
const _notify = (subs, ...a) => subs.forEach((fn) => { try { fn(...a); } catch (_) { /* a dead subscriber */ } });
const RecordComposerHost = {
  get: (kind, id) => _composerHosts.get(shellCore().draftKey(kind, id)) || 'pane',
  claim: (kind, id, host) => {
    const key = shellCore().draftKey(kind, id);
    const next = host === 'workspace' ? 'workspace' : 'pane';
    if ((_composerHosts.get(key) || 'pane') === next) return;
    if (next === 'pane') _composerHosts.delete(key); else _composerHosts.set(key, next);
    _notify(_composerHostSubs);
  },
  subscribe: (fn) => { _composerHostSubs.add(fn); return () => { _composerHostSubs.delete(fn); }; },
  reveal: (kind, id, sel) => _notify(_composerRevealSubs, shellCore().draftKey(kind, id), sel || null),
  onReveal: (fn) => { _composerRevealSubs.add(fn); return () => { _composerRevealSubs.delete(fn); }; },
};
const _paneHost = () => 'pane';
const useComposerHost = (kind, id) => React.useSyncExternalStore(RecordComposerHost.subscribe,
  () => RecordComposerHost.get(kind, id), _paneHost);

// What the shell is doing, for the slots (step 10's cards render one-line rows in mode 'one').
const RecordShellContext = React.createContext(null);
const useRecordShell = () => React.useContext(RecordShellContext);

const SHELL_ICON_BTN = { width: 26, height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 0, flexShrink: 0 };

// ── Last touch: 'Last touch 3d · Email · Jordan'; a click opens the note (?note=).
const LastTouch = ({ lt }) => {
  const text = shellCore().lastTouchText(lt, Date.now());
  if (!text) return <span data-last-touch="" style={{ color: 'var(--text-4)' }}>Last touch —</span>;
  const title = [new Date(lt.at).toLocaleString(), lt.author || null].filter(Boolean).join(' · ');
  return (
    <button type="button" data-last-touch="" title={title}
      onClick={() => { if (lt.note_id) window.openRecordNote(lt.note_id, { via: 'button' }); }}
      style={{ border: 'none', background: 'transparent', padding: 0, cursor: lt.note_id ? 'pointer' : 'default',
        color: 'var(--text-3)', fontSize: 'inherit', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
      {text}
    </button>
  );
};

// ── ≤3 pills in priority order; the rest behind '+N' (a body portal at z400).
const PILL_SHOWN = 3;
const HeaderPills = ({ pills, hidden = false, panelName = null }) => {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const btnRef = React.useRef(null);
  const popRef = React.useRef(null);
  const list = (pills || []).filter(Boolean);
  const rest = list.slice(PILL_SHOWN);
  const close = React.useCallback(() => { setOpen(false); setPos(null); }, []);
  window.useEscapeKey(open, close);
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, (window.innerWidth || 1280) - 328)) });
  }, [open]);
  React.useEffect(() => {
    if (!open) return undefined;
    const inside = (t) => (popRef.current && popRef.current.contains(t)) || (btnRef.current && btnRef.current.contains(t));
    const onDown = (e) => { if (!inside(e.target)) close(); };
    const onScroll = (e) => { if (!(popRef.current && popRef.current.contains(e.target))) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);
  React.useEffect(() => { if (open && (hidden || !rest.length)) close(); }, [open, hidden, rest.length, close]);
  // A pill in the popover opened (or switched) a pop-out (the blast pill → Marketing): close, or it floats over the drawer at z 400.
  React.useEffect(() => { if (panelName) close(); }, [panelName, close]);
  if (!list.length) return null;
  return (
    <span data-record-pills="" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
      {list.slice(0, PILL_SHOWN).map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}
      {rest.length > 0 && (
        <button ref={btnRef} type="button" aria-haspopup="true" aria-expanded={open}
          onClick={() => (open ? close() : setOpen(true))}
          style={{ border: '1px solid var(--border)', borderRadius: 999, background: 'var(--surface)', color: 'var(--text-3)',
            fontSize: 11, padding: '1px 7px', cursor: 'pointer', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>+{rest.length}</button>
      )}
      {open && pos && ReactDOM.createPortal(
        <div ref={popRef} data-record-pills-more="" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: shellCore().Z.menu,
          maxWidth: 320, display: 'flex', flexWrap: 'wrap', gap: 6, padding: 8, background: 'var(--surface)',
          border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-lg)' }}>
          {rest.map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}
        </div>,
        document.body,
      )}
    </span>
  );
};

// ── the header: row 1 (normal flow) + the overlay (absolute under it, opaque).
// Presentational; RecordPage injects `compact` and `overlayRef`.
const RecordHeader = ({ crumb = null, logo = null, title = null, pills = null, terminal = null, stage = null,
  primaries = null, log = null, menu = null, meta = null, lastTouch, banner = null, band = null,
  compact = false, overlayRef = null, panelName = null }) => {
  const rowEl = React.useRef(null);
  const overlayEl = React.useRef(null);
  const first = React.useRef(true);
  const setOverlay = React.useCallback((el) => {
    overlayEl.current = el;
    if (typeof overlayRef === 'function') overlayRef(el);
  }, [overlayRef]);
  // Rows 2+ fade past 48px of center scroll and back under 8: opacity only (no height, no scrollTop).
  React.useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (overlayEl.current && window.animateEl) window.animateEl(overlayEl.current, { opacity: compact ? 0 : 1 }, { duration: shellCore().SHELL.fade });
  }, [compact]);
  // Faded rows leave the Tab order too (React 18 has no inert prop); focus inside them moves to row 1.
  React.useLayoutEffect(() => {
    const el = overlayEl.current;
    if (!el) return;
    if (!compact) { el.removeAttribute('inert'); return; }
    if (rowEl.current && el.contains(document.activeElement)) { try { rowEl.current.focus({ preventScroll: true }); } catch (_) { /* detached */ } }
    el.setAttribute('inert', '');
  }, [compact]);
  const S = shellCore().SHELL;
  const canWrite = window.recordCanWrite();
  // Primaries are the caller's budget (2; 3 on a referral-role contact); 3 is the most row 1 takes.
  const prim = (primaries || []).filter(Boolean).slice(0, 3);
  const hasMeta = meta != null || lastTouch !== undefined;
  const hasOverlay = hasMeta || !!banner || !!band;
  return (
    <>
      <header ref={rowEl} tabIndex={-1} data-record-header="" data-compact={compact ? '1' : '0'}
        style={{ height: S.row1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', minWidth: 0,
          background: 'var(--surface)', borderBottom: '1px solid var(--border)', outline: 'none' }}>
        {crumb && (
          <button type="button" data-record-crumb="" onClick={crumb.onClick}
            style={{ display: compact ? 'none' : 'inline-flex', alignItems: 'center', border: 'none', background: 'transparent',
              padding: 0, cursor: 'pointer', color: 'var(--text-3)', fontSize: 12, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
            ‹ {crumb.label}
          </button>
        )}
        {terminal && <span data-record-terminal="" style={{ display: compact ? 'inline-flex' : 'none', flexShrink: 0 }}>{terminal}</span>}
        {logo && <span style={{ display: 'inline-flex', width: 32, height: 32, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>{logo}</span>}
        <div data-record-title="" style={{ minWidth: 0, flex: '0 1 auto', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{title}</div>
        <span style={{ display: compact ? 'none' : 'inline-flex', minWidth: 0, flexShrink: 1 }}><HeaderPills pills={pills} hidden={compact} panelName={panelName} /></span>
        {stage && <span data-record-stage="" style={{ display: compact ? 'inline-flex' : 'none', flexShrink: 0 }}>{stage}</span>}
        <div style={{ flex: 1 }} />
        <div data-record-controls="" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {prim.map((p, i) => <React.Fragment key={i}>{p}</React.Fragment>)}
          {canWrite && log}
          {canWrite && menu}
        </div>
      </header>
      <div ref={setOverlay} data-record-overlay="" aria-hidden={compact ? 'true' : undefined}
        style={{ position: 'absolute', top: S.row1, left: 0, right: 0, zIndex: 3, background: 'var(--surface)',
          pointerEvents: compact ? 'none' : 'auto',
          ...(hasOverlay ? { padding: '6px 16px 8px', borderBottom: '1px solid var(--border)' } : null) }}>
        {hasMeta && (
          <div data-record-meta="" style={{ height: 20, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden',
            whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-3)' }}>
            {meta}
            {meta != null && lastTouch !== undefined && <span aria-hidden="true" style={{ color: 'var(--text-4)' }}>·</span>}
            {lastTouch !== undefined && <LastTouch lt={lastTouch} />}
          </div>
        )}
        {banner && <div data-record-banner="" style={{ marginTop: hasMeta ? 6 : 0 }}>{banner}</div>}
        {band && <div data-record-band="" style={{ marginTop: hasMeta || banner ? 8 : 0 }}>{band}</div>}
      </div>
    </>
  );
};

// ── a folded side column: a 40px strip of its card icons (each opens that card's panel).
const RailStrip = ({ side, items, onExpand }) => (
  <div data-record-strip={side} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '6px 0' }}>
    <button type="button" data-record-expand={side} aria-label="Expand" title="Expand" onClick={onExpand} style={SHELL_ICON_BTN}>
      {side === 'left' ? <Icon.ChevronR size={12} /> : <Icon.ChevronL size={12} />}
    </button>
    {(items || []).filter(Boolean).map((it) => (
      <button key={it.key} type="button" aria-label={it.label} title={it.label} onClick={it.onOpen} style={SHELL_ICON_BTN}>{it.icon}</button>
    ))}
  </div>
);

// ── fold / unfold the right column's cards only (Section's ids filter). Up/down chevrons:
// the column's own fold (beside it) points sideways.
const FoldAllPair = ({ ids }) => {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : [];
  if (!list.length) return null;
  const fire = (open) => window.dispatchEvent(new CustomEvent('atlas-sections', { detail: { open, ids: list } }));
  return (
    <span data-fold-all="" style={{ display: 'inline-flex', gap: 2 }}>
      <button type="button" data-fold-all="open" aria-label="Expand all" title="Expand all" onClick={() => fire(true)} style={SHELL_ICON_BTN}>
        <Icon.ChevronD size={12} />
      </button>
      <button type="button" data-fold-all="close" aria-label="Collapse all" title="Collapse all" onClick={() => fire(false)} style={SHELL_ICON_BTN}>
        <Icon.ChevronD size={12} style={{ transform: 'rotate(180deg)' }} />
      </button>
    </span>
  );
};

// ── the composer pane: pinned at the top of the center scroller (55% of the column,
// ≥ 240, a bottom drag handle, its own scroll); ⤢ = the same element as a 760px
// modeless fixed panel over the center and right columns. Mouse only. No onFloat
// (a reference drawer is open): docked, no ⤢.
// The field it focuses is the composer's own pick (RecordComposer's data-composer-focus:
// the body, or the task's first field), else its first field. autoFocus false: the draft
// was already open when the page mounted (returning to a record never steals focus).
const COMPOSER_FIELD = 'textarea, input:not([type="hidden"]):not([type="checkbox"]):not([type="file"])';
const composerField = (el, sel) => {
  if (!el) return null;
  const box = el.querySelector('[data-composer-focus]');
  const want = sel || (box && box.getAttribute('data-composer-focus'));
  let f = null;
  if (want) { try { f = (box || el).querySelector(want); } catch (_) { f = null; } }
  return f || el.querySelector(COMPOSER_FIELD);
};
const focusField = (f) => { if (f) { try { f.focus({ preventScroll: true }); } catch (_) { /* detached */ } } };
const ComposerPane = ({ height, floating = false, rect = null, onFloat = null, onResize, autoFocus = true, revealKey = null, children }) => {
  const ref = React.useRef(null);
  const drag = React.useRef(null);
  const resizeRef = React.useRef(onResize);
  resizeRef.current = onResize;
  // Opened: its field takes focus, unless something inside already has it.
  React.useEffect(() => {
    const el = ref.current;
    if (!autoFocus || !el || el.contains(document.activeElement)) return;
    focusField(composerField(el));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // A compose request while open (another kind, or Update): after that render, focus its field.
  React.useEffect(() => (revealKey ? RecordComposerHost.onReveal((key, sel) => {
    if (key !== revealKey) return;
    requestAnimationFrame(() => focusField(composerField(ref.current, sel)));
  }) : undefined), [revealKey]);
  React.useEffect(() => {
    const onMove = (e) => { const d = drag.current; if (d && resizeRef.current) resizeRef.current(d.h + (e.clientY - d.y)); };
    const onUp = () => {
      if (!drag.current) return;
      drag.current = null; document.body.style.cursor = ''; document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);
  const float = !!(floating && rect);
  const startDrag = (e) => {
    if (float || (e.button != null && e.button !== 0)) return;
    e.preventDefault();
    drag.current = { y: e.clientY, h: ref.current ? ref.current.offsetHeight : height };
    document.body.style.cursor = 'row-resize'; document.body.style.userSelect = 'none';
  };
  const wrap = float
    ? { position: 'fixed', left: rect.left, top: rect.top, width: rect.width, height: rect.height, zIndex: shellCore().Z.drawer - 1 }
    : { position: 'sticky', top: 0, zIndex: 2, height, padding: '10px 16px', background: 'var(--surface-2)' };
  return (
    <div ref={ref} data-record-composer-pane={float ? 'float' : 'pane'} style={{ boxSizing: 'border-box', ...wrap }}>
      <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--surface)',
        border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', boxShadow: float ? 'var(--shadow-lg)' : 'none' }}>
        <div data-record-composer-body="" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 14px' }}>{children}</div>
        <div data-record-composer-handle="" onMouseDown={startDrag}
          style={{ flexShrink: 0, height: 16, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderTop: '1px solid var(--border)', background: 'var(--surface-2)', cursor: float ? 'default' : 'row-resize' }}>
          {!float && <span aria-hidden="true" style={{ width: 32, height: 3, borderRadius: 2, background: 'var(--border-strong)' }} />}
          {onFloat && (
            <button type="button" aria-label={float ? 'Dock' : 'Widen'} title={float ? 'Dock' : 'Widen'}
              onMouseDown={(e) => e.stopPropagation()} onClick={onFloat}
              style={{ ...SHELL_ICON_BTN, width: 22, height: 14, position: 'absolute', right: 6, top: 1 }}>
              <Icon.Maximize size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── the page
const SIDE_MODES = ['desk', 'three'];          // side columns exist (and fold); dividers on 'desk' only
const EMPTY_ACT = { gridArea: 'act' };

const RecordPage = ({ kind, id, header = null, left = null, act = null, feed = null, right = null, cardIds = null,
  rails = null, composer: composerProp = null, panel = null, onNoteChange }) => {
  const core = shellCore();
  const device = window.useDevice();
  const touch = !!device.touch;
  const bodyRef = React.useRef(null);
  const centerRef = React.useRef(null);
  const rightRef = React.useRef(null);
  const drag = React.useRef(null);                 // divider drag: core.dividerDragStart + { x, last }
  const [bodyW, setBodyW] = React.useState(() => Math.max(0, (window.innerWidth || 1280) - (device.compact ? 0 : core.FIT.sidebar)));
  const [bodyH, setBodyH] = React.useState(0);
  const [overlayEl, setOverlayEl] = React.useState(null);
  const [overlayH, setOverlayH] = React.useState(0);
  const [compact, setCompact] = React.useState(false);
  const [widths, setWidths] = React.useState(() => loadWidths(kind));
  const [dragW, setDragW] = React.useState(null);  // a divider drag's shown { L, R }; null when not dragging
  const [collapsed, setCollapsed] = React.useState(() => core.readPaneCollapsed(_lsGet(collapsedKey(kind))));
  const [dragH, setDragH] = React.useState(null);  // the composer pane's dragged height: memory only
  const [floating, setFloating] = React.useState(false);
  const [floatRect, setFloatRect] = React.useState(null);
  const host = useComposerHost(kind, id);
  const rec = React.useMemo(() => ({ kind, id }), [kind, id]);
  const pp = window.usePanelParam(rec);            // the open panel: a reference drawer docks a floated composer
  const overlayRef = React.useCallback((el) => setOverlayEl(el), []);
  // A draft already open when the page mounts (back on this record) doesn't take focus.
  const quietOpen = React.useRef(!!(composerProp && composerProp.open));

  // A drag shows widths that fit as they are (dividerDragStart), so auto-fit never moves them.
  const plan = core.recordGridPlan({ kind, bodyW, touch, widths: dragW || widths, collapsed });
  const st = core.recordShellStyles(plan, { overlayH, bodyH });
  const sided = SIDE_MODES.includes(plan.mode);
  // The record's one composer: a pane on a mouse, a modeless drawer on touch,
  // nowhere while a workspace holds it (RecordComposerHost).
  const composer = {
    open: !!(composerProp && composerProp.open && composerProp.element) && host === 'pane',
    element: (composerProp && composerProp.element) || null,
    title: (composerProp && composerProp.title) || null,
  };
  const paneOpen = composer.open && !touch;
  // A reference drawer (right-anchored) would cover a floated composer: while one is
  // open the composer docks into the pane and ⤢ hides; it floats again when it closes.
  const refMeta = core.panelMeta(pp.name);
  const refDrawer = !!(refMeta && refMeta.form === 'drawer');
  const floatNow = floating && !refDrawer;

  // The body's own size (the sidebar may or may not be docked) drives the fit on every device.
  React.useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const take = () => {
      const w = Math.round(el.clientWidth); const h = Math.round(el.clientHeight);
      if (w > 0) setBodyW((p) => (p === w ? p : w));
      if (h > 0) setBodyH((p) => (p === h ? p : h));
    };
    take();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', take);
      return () => window.removeEventListener('resize', take);
    }
    const ro = new ResizeObserver(take);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // The overlay's height is every column's top spacer.
  React.useLayoutEffect(() => {
    if (!overlayEl) return undefined;
    const take = () => { const h = Math.round(overlayEl.offsetHeight); setOverlayH((p) => (p === h ? p : h)); };
    take();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(take);
    ro.observe(overlayEl);
    return () => ro.disconnect();
  }, [overlayEl]);
  // Dividers (mouse): the dragged side follows the pointer from its shown width (240–440,
  // the center ≥ 420), the other side holds still; saved on mouseup if the side moved.
  React.useEffect(() => {
    if (touch) return undefined;   // touch: no dividers, never saves widths
    const onMove = (e) => {
      const d = drag.current; if (!d) return;
      const w = core.dividerDragWidth(d, e.clientX - d.x);
      if (w === d.last) return;
      d.last = w;
      setDragW(core.dividerDragWidths(d, w));
    };
    const onUp = () => {
      const d = drag.current; if (!d) return;
      drag.current = null;
      document.body.style.cursor = ''; document.body.style.userSelect = '';
      setDragW(null);
      const next = core.dividerDragWidths(d, d.last);
      if (!next) return;   // a click, or back where it started
      setWidths(next);
      saveWidths(kind, next);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [touch, kind]);   // eslint-disable-line react-hooks/exhaustive-deps
  // A mode change re-reads compaction from the scroller that now drives it.
  const modeRef = React.useRef(plan.mode);
  React.useEffect(() => {
    if (modeRef.current === plan.mode) return;
    modeRef.current = plan.mode;
    const src = plan.mode === 'one' ? bodyRef.current : centerRef.current;
    setCompact(core.headerCompactNext(false, src ? src.scrollTop : 0));
  }, [plan.mode]);   // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => { if (!composer.open && floating) setFloating(false); }, [composer.open, floating]);
  React.useEffect(() => { if (!paneOpen) quietOpen.current = false; }, [paneOpen]);
  React.useLayoutEffect(() => {
    if (!paneOpen || !floatNow || !centerRef.current) { setFloatRect((p) => (p == null ? p : null)); return; }
    const c = centerRef.current.getBoundingClientRect();
    const rw = rightRef.current ? rightRef.current.getBoundingClientRect().width : 0;
    const next = core.composerFloatRect({ left: c.left, top: c.top, width: c.width, height: c.height }, rw, window.innerWidth);
    setFloatRect((p) => (p && p.left === next.left && p.top === next.top && p.width === next.width && p.height === next.height ? p : next));
  }, [paneOpen, floatNow, bodyW, bodyH, plan.L, plan.R]);   // eslint-disable-line react-hooks/exhaustive-deps
  // bodyW: step 13's contact header folds Email ▾ under a 1180px body.
  const shell = React.useMemo(() => ({ kind, id, mode: plan.mode, compact, touch, bodyW }), [kind, id, plan.mode, compact, touch, bodyW]);

  const startDrag = (side) => (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    drag.current = { ...core.dividerDragStart(plan, widths, side), x: e.clientX, last: null };
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  const setSide = (side, on) => {
    const next = { left: side === 'left' ? !!on : collapsed.left, right: side === 'right' ? !!on : collapsed.right };
    setCollapsed(next);
    _lsSet(collapsedKey(kind), JSON.stringify(next));
  };
  // Compaction follows the center scroller ('one': the body, the only scroller).
  const onCenterScroll = (e) => {
    if (plan.mode === 'one') return;
    const t = e.currentTarget.scrollTop;
    setCompact((c) => core.headerCompactNext(c, t));
  };
  const onBodyScroll = (e) => {
    if (plan.mode !== 'one') return;
    const t = e.currentTarget.scrollTop;
    setCompact((c) => core.headerCompactNext(c, t));
  };
  const closeComposer = () => {
    if (composerProp && composerProp.onClose) { composerProp.onClose(); return; }
    const K = window.RecordNotesKit;
    if (window.RecordDrafts && K) window.RecordDrafts.set(K.draftKeyFor(kind, id), K.closeDraft);
  };
  const el = composer.element;
  const drawerEl = touch && React.isValidElement(el) && el.props && el.props.presentation === 'inline'
    ? React.cloneElement(el, { presentation: 'drawer' }) : el;

  return (
    <RecordShellContext.Provider value={shell}>
      <div data-record-page={kind} data-record-mode={plan.mode}
        style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
          background: 'var(--surface-2)' }}>
        <RecordHeader {...(header || {})} compact={compact} overlayRef={overlayRef} panelName={pp.name} />
        <div ref={bodyRef} data-record-body="" onScroll={onBodyScroll} style={st.body}>
          <div data-record-grid="" style={st.grid}>
            {plan.dividers && !plan.collapsed.left && (
              <div data-record-divider="left" onMouseDown={startDrag('left')} title="Drag to resize"
                style={{ position: 'absolute', top: 0, bottom: 0, left: plan.L - 3, width: 7, cursor: 'col-resize', zIndex: 2 }} />
            )}
            {plan.dividers && !plan.collapsed.right && (
              <div data-record-divider="right" onMouseDown={startDrag('right')} title="Drag to resize"
                style={{ position: 'absolute', top: 0, bottom: 0, right: plan.R - 3, width: 7, cursor: 'col-resize', zIndex: 2 }} />
            )}
            <div data-record-col="left" style={st.left}>
              <div data-record-spacer="left" aria-hidden="true" style={{ height: st.spacer.left }} />
              <div data-record-tools="left" style={{ display: sided && !plan.collapsed.left ? 'flex' : 'none', justifyContent: 'flex-end', padding: '4px 6px 0' }}>
                <button type="button" data-record-collapse="left" aria-label="Collapse" title="Collapse" onClick={() => setSide('left', true)} style={SHELL_ICON_BTN}>
                  <Icon.ChevronL size={12} />
                </button>
              </div>
              {plan.collapsed.left && <RailStrip side="left" items={rails && rails.left} onExpand={() => setSide('left', false)} />}
              <div data-record-col-body="left" style={{ display: plan.collapsed.left ? 'none' : 'block' }}>{left}</div>
            </div>
            <div ref={centerRef} data-record-col="center" onScroll={onCenterScroll} style={st.center}>
              <div data-record-spacer="center" aria-hidden="true" style={{ height: st.spacer.center, gridArea: 'spacer' }} />
              {paneOpen && (
                <ComposerPane height={core.composerPaneHeight(bodyH, dragH)} floating={floatNow} rect={floatRect}
                  onFloat={refDrawer ? null : () => setFloating((f) => !f)} onResize={setDragH}
                  autoFocus={!quietOpen.current} revealKey={core.draftKey(kind, id)}>
                  {composer.element}
                </ComposerPane>
              )}
              <div data-record-act="" style={act == null ? EMPTY_ACT : st.act}>{act}</div>
              <div data-record-feed="" style={st.feed}>{feed}</div>
            </div>
            <div ref={rightRef} data-record-col="right" style={st.right}>
              <div data-record-spacer="right" aria-hidden="true" style={{ height: st.spacer.right }} />
              <div data-record-tools="right" style={{ display: plan.collapsed.right ? 'none' : 'flex', alignItems: 'center', padding: '4px 6px 0' }}>
                {sided && (
                  <button type="button" data-record-collapse="right" aria-label="Collapse" title="Collapse" onClick={() => setSide('right', true)} style={SHELL_ICON_BTN}>
                    <Icon.ChevronR size={12} />
                  </button>
                )}
                <span style={{ marginLeft: 'auto' }}><FoldAllPair ids={cardIds} /></span>
              </div>
              {plan.collapsed.right && <RailStrip side="right" items={rails && rails.right} onExpand={() => setSide('right', false)} />}
              <div data-record-col-body="right" style={{ display: plan.collapsed.right ? 'none' : 'block' }}>{right}</div>
            </div>
          </div>
        </div>
        {touch && composer.element && (
          <window.RecordDrawer name="composer" modeless open={composer.open} onClose={closeComposer}
            title={composer.title} width={560}>
            {drawerEl}
          </window.RecordDrawer>
        )}
        <window.PanelHost kind={kind} id={id} record={(panel && panel.record) || null} facts={(panel && panel.facts) || null}
          variant={(panel && panel.variant) || null} ctx={(panel && panel.ctx) || null}
          onPanelClose={(panel && panel.onPanelClose) || undefined} modeless={composer.open && !device.touch} />
        <window.NoteHost kind={kind} id={id} onChange={onNoteChange} />
      </div>
    </RecordShellContext.Provider>
  );
};

Object.assign(window, {
  RecordPage, RecordHeader, LastTouch, ComposerPane, FoldAllPair, RecordComposerHost, useComposerHost,
  RecordShellContext, useRecordShell,
});
